<script setup lang="ts">
import { computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import { object, number } from 'yup';
import { useMutation } from '@tanstack/vue-query';
import { shiftOfferingsApi, type ShiftOfferingCreateForm } from '@/api/shift-offerings';
import { type Caregiver } from '@/api/caregivers';
import { type Shift } from '@/api/shifts';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';

const props = defineProps<{
  selectedCaregivers: Caregiver[];
  shift?: Shift;
  patientId?: number;
  startDatetime?: string;
  endDatetime?: string;
}>();

const emit = defineEmits<{
  close: [];
  success: [];
}>();

const { trackShiftEvent } = useAnalytics();

const isReplacement = computed(() => !!props.shift);

const validationSchema = object({
  slots: number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .required('Los cupos son requeridos')
    .min(1, 'Debe haber al menos 1 cupo'),
});

const { handleSubmit, errors, defineField, meta } = useForm({
  validationSchema,
  initialValues: {
    slots: 1,
  },
});

const [slots, slotsAttrs] = defineField('slots');

const showSlots = ref(false);

const formPatientId = props.shift?.patientId ?? props.patientId;
const formStartDatetime = props.shift?.startDatetime ?? props.startDatetime;
const formEndDatetime = props.shift?.endDatetime ?? props.endDatetime;

const { mutate: createOffering, isPending } = useMutation({
  mutationFn: (form: ShiftOfferingCreateForm) => shiftOfferingsApi.create(form),
  onSuccess: () => {
    const eventName = isReplacement.value ?
      AnalyticsEvents.shifts.offeringExistingEmail :
      AnalyticsEvents.shifts.offeringPatientEmail;
    trackShiftEvent(eventName, {
      caregiverCount: props.selectedCaregivers.length,
      slots: slots.value,
    }, props.shift?.id);
    emit('success');
  },
});

const onSubmit = handleSubmit((values) => {
  if (!formPatientId || !formStartDatetime || !formEndDatetime) return;

  createOffering({
    patientId: formPatientId,
    startDatetime: formStartDatetime,
    endDatetime: formEndDatetime,
    slots: values.slots!,
    caregiverIds: props.selectedCaregivers.map(c => c.id),
    ...(props.shift?.id ? { shiftId: props.shift.id } : {}),
  });
});
</script>

<template>
  <v-card>
    <v-card-title>Enviar Oferta de Turno</v-card-title>
    <v-card-text>
      <v-alert
        type="info"
        variant="tonal"
        class="mb-4"
      >
        Se enviará un email a
        <strong>
          {{ selectedCaregivers.length }}
          {{ selectedCaregivers.length === 1 ? 'profesional' : 'profesionales' }}
        </strong>.
        <template v-if="(slots ?? 1) <= 1">
          El primero en aceptar será asignado al turno automáticamente.
        </template>
        <template v-else>
          Los primeros <strong>{{ slots }}</strong> en aceptar
          serán asignados al turno automáticamente.
        </template>
      </v-alert>

      <div v-if="!isReplacement">
        <a
          class="text-body-2 cursor-pointer"
          @click="showSlots = !showSlots"
        >
          {{ showSlots ? '▾' : '▸' }} ¿Necesitas más de un profesional?
        </a>
        <v-row
          v-if="showSlots"
          class="mt-2"
        >
          <v-col cols="6">
            <v-text-field
              v-model.number="slots"
              v-bind="slotsAttrs"
              label="Cupos"
              type="number"
              min="1"
              :error-messages="errors.slots"
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        text="Cancelar"
        @click="emit('close')"
      />
      <v-btn
        color="primary"
        text="Enviar Oferta"
        :loading="isPending"
        :disabled="!meta.valid || selectedCaregivers.length === 0"
        @click="onSubmit"
      />
    </v-card-actions>
  </v-card>
</template>
